import { useCallback, useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Copy, Check, ExternalLink, Globe, Loader2, RefreshCw, Trash2 } from 'lucide-react'
import {
  addAppCustomDomain,
  listAppCustomDomains,
  removeAppCustomDomain,
  verifyAppCustomDomain,
  type AppCustomDomain,
} from '../lib/api'
import { formatRelativeTime } from '../lib/format'
import { ConfirmDialog } from './ui/ConfirmDialog'

// =============================================================================
// Props
// =============================================================================

interface CustomDomainsSectionProps {
  appName: string
  /** Whether the current user can manage custom domains (owner or admin). */
  canManage: boolean
}

interface VerifyNote {
  resolves: boolean
  detail: string
}

// =============================================================================
// Component
// =============================================================================

/**
 * Custom domain management: attach a domain to this app, show DNS setup
 * instructions (CNAME + TXT), verify ownership, and remove domains.
 */
export default function CustomDomainsSection({ appName, canManage }: CustomDomainsSectionProps) {
  const [domains, setDomains] = useState<AppCustomDomain[]>([])
  const [dnsTarget, setDnsTarget] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [newDomainInput, setNewDomainInput] = useState('')
  const [adding, setAdding] = useState(false)
  const [verifyingId, setVerifyingId] = useState<number | null>(null)
  const [removingId, setRemovingId] = useState<number | null>(null)
  const [expandedDomains, setExpandedDomains] = useState<Set<number>>(new Set())
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [verifyNotes, setVerifyNotes] = useState<Record<number, VerifyNote | undefined>>({})

  const loadDomains = useCallback(async () => {
    if (!canManage) {
      setDomains([])
      return
    }

    setLoading(true)
    setError(null)
    try {
      const result = await listAppCustomDomains(appName)
      const rows = result.domains || []
      setDomains(rows)
      setDnsTarget(result.dns_target)
      setExpandedDomains(new Set(rows.filter((d) => d.status === 'pending').map((d) => d.id)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load custom domains')
    } finally {
      setLoading(false)
    }
  }, [appName, canManage])

  // Reset transient editor state when the app slug changes.
  useEffect(() => {
    setSuccess(null)
    setError(null)
    setNewDomainInput('')
    setVerifyNotes({})
    setExpandedDomains(new Set())
  }, [appName])

  useEffect(() => {
    loadDomains()
  }, [loadDomains])

  // Refresh on window focus so DNS propagation checked elsewhere stays in sync.
  useEffect(() => {
    function onFocus() {
      loadDomains()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [loadDomains])

  function toggleDomainExpanded(id: number) {
    setExpandedDomains((current) => {
      const next = new Set(current)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  async function copyToClipboard(key: string, value: string) {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedField(key)
      setTimeout(() => setCopiedField((current) => (current === key ? null : current)), 2000)
    } catch {
      // Clipboard API can be unavailable (insecure context, permissions); silently ignore.
    }
  }

  async function handleAddDomain() {
    const domain = newDomainInput.trim().toLowerCase()
    if (!domain) {
      setError('Enter a domain name')
      return
    }
    setAdding(true)
    setError(null)
    setSuccess(null)
    try {
      await addAppCustomDomain(appName, domain)
      setSuccess(`"${domain}" added — follow the DNS setup instructions below to verify it`)
      setNewDomainInput('')
      await loadDomains()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add domain')
    } finally {
      setAdding(false)
    }
  }

  async function handleVerify(domain: AppCustomDomain) {
    setVerifyingId(domain.id)
    setError(null)
    setSuccess(null)
    try {
      const result = await verifyAppCustomDomain(appName, domain.id)
      setVerifyNotes((current) => ({
        ...current,
        [domain.id]: { resolves: result.resolves, detail: result.detail },
      }))
      if (result.verified) {
        setSuccess(result.detail)
      } else {
        setError(result.detail)
      }
      await loadDomains()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify domain')
    } finally {
      setVerifyingId(null)
    }
  }

  async function handleRemove(domain: AppCustomDomain) {
    setRemovingId(domain.id)
    setError(null)
    setSuccess(null)
    try {
      await removeAppCustomDomain(appName, domain.id)
      setSuccess(`"${domain.domain}" removed`)
      setVerifyNotes((current) => {
        const next = { ...current }
        delete next[domain.id]
        return next
      })
      await loadDomains()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove domain')
    } finally {
      setRemovingId(null)
    }
  }

  if (!canManage) {
    return null
  }

  return (
    <div className="p-6 rounded-xl border border-border bg-card">
      <h2 className="text-lg font-semibold flex items-center gap-2 mb-1">
        <Globe className="w-5 h-5 text-primary" />
        Custom Domains
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Serve this app directly on your own domain. Platform sign-in still applies.
      </p>

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-destructive/10 text-destructive text-sm border border-destructive/20">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-3 p-3 rounded-lg bg-green-500/10 text-green-500 text-sm border border-green-500/20">
          {success}
        </div>
      )}

      <div className="mb-4 flex items-center gap-2">
        <input
          type="text"
          value={newDomainInput}
          onChange={(e) => setNewDomainInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !adding) {
              handleAddDomain()
            }
          }}
          placeholder="app.example.com"
          autoComplete="off"
          spellCheck={false}
          disabled={adding}
          className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
        />
        <button
          type="button"
          onClick={handleAddDomain}
          disabled={adding}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {adding && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Add
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading domains...
        </div>
      ) : domains.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No custom domains yet. Add one above to serve this app on your own domain.
        </p>
      ) : (
        <div className="space-y-3">
          {domains.map((domain) => {
            const expanded = expandedDomains.has(domain.id)
            const verifying = verifyingId === domain.id
            const removing = removingId === domain.id
            const verified = domain.status === 'verified'
            const note = verifyNotes[domain.id]
            return (
              <div key={domain.id} className="rounded-lg border border-border">
                <div className="flex items-start justify-between gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {verified ? (
                        <a
                          href={`https://${domain.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-sm text-foreground hover:underline inline-flex items-center gap-1 break-all"
                        >
                          {domain.domain}
                          <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                        </a>
                      ) : (
                        <span className="font-mono text-sm text-foreground break-all">{domain.domain}</span>
                      )}
                      <span
                        className={`px-2 py-1 rounded-md text-[11px] border shrink-0 ${
                          verified
                            ? 'border-green-500/30 text-green-500 bg-green-500/10'
                            : 'border-amber-500/30 text-amber-500 bg-amber-500/10'
                        }`}
                      >
                        {verified ? 'Active' : 'Pending verification'}
                      </span>
                    </div>
                    {domain.last_error && (
                      <p className="text-xs text-destructive mt-1">
                        {domain.last_error}
                        {domain.last_checked_at && (
                          <span className="text-muted-foreground">
                            {' '}
                            &middot; last checked {formatRelativeTime(domain.last_checked_at)}
                          </span>
                        )}
                      </p>
                    )}
                    {note && !note.resolves && (
                      <p className="text-xs text-amber-500 mt-1">
                        This domain doesn't resolve to the platform yet — the CNAME record may still
                        be missing or propagating.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleVerify(domain)}
                      disabled={verifying || removing}
                      title={verified ? 'Re-check DNS resolution' : 'Verify DNS records'}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors disabled:opacity-50 ${
                        verified
                          ? 'border border-border hover:bg-secondary'
                          : 'bg-primary text-primary-foreground hover:bg-primary/90'
                      }`}
                    >
                      {verifying ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : verified ? (
                        <RefreshCw className="w-3 h-3" />
                      ) : null}
                      {verified ? 'Re-check' : 'Verify'}
                    </button>
                    <ConfirmDialog
                      trigger={
                        <button
                          type="button"
                          disabled={verifying || removing}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-destructive/30 text-destructive text-xs hover:bg-destructive/10 transition-colors disabled:opacity-50"
                          title="Remove this domain"
                        >
                          {removing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        </button>
                      }
                      title={`Remove "${domain.domain}"?`}
                      description="The domain will stop routing to this app. TLS certificates and DNS verification state are discarded; you would need to re-verify if you add it again."
                      confirmLabel="Remove"
                      loadingLabel="Removing..."
                      variant="destructive"
                      onConfirm={() => handleRemove(domain)}
                    />
                  </div>
                </div>

                {!verified && (
                  <div className="border-t border-border/60">
                    <button
                      type="button"
                      onClick={() => toggleDomainExpanded(domain.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-secondary/40 transition-colors"
                    >
                      {expanded ? (
                        <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                      )}
                      DNS setup
                    </button>
                    {expanded && (
                      <div className="px-3 pb-3 space-y-2">
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="text-muted-foreground">
                                <th className="text-left font-medium py-1 pr-3">Type</th>
                                <th className="text-left font-medium py-1 pr-3">Name</th>
                                <th className="text-left font-medium py-1">Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-t border-border/60">
                                <td className="py-2 pr-3 align-top font-mono">CNAME</td>
                                <td className="py-2 pr-3 align-top font-mono break-all">{domain.domain}</td>
                                <td className="py-2 align-top">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono break-all">{dnsTarget}</span>
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(`${domain.id}-cname`, dnsTarget)}
                                      className="p-1 rounded border border-border hover:bg-secondary transition-colors shrink-0"
                                      title="Copy value"
                                    >
                                      {copiedField === `${domain.id}-cname` ? (
                                        <Check className="w-3 h-3 text-green-500" />
                                      ) : (
                                        <Copy className="w-3 h-3" />
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              <tr className="border-t border-border/60">
                                <td className="py-2 pr-3 align-top font-mono">TXT</td>
                                <td className="py-2 pr-3 align-top">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono break-all">{domain.txt_record_name}</span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        copyToClipboard(`${domain.id}-txt-name`, domain.txt_record_name)
                                      }
                                      className="p-1 rounded border border-border hover:bg-secondary transition-colors shrink-0"
                                      title="Copy name"
                                    >
                                      {copiedField === `${domain.id}-txt-name` ? (
                                        <Check className="w-3 h-3 text-green-500" />
                                      ) : (
                                        <Copy className="w-3 h-3" />
                                      )}
                                    </button>
                                  </div>
                                </td>
                                <td className="py-2 align-top">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono break-all">{domain.txt_record_value}</span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        copyToClipboard(`${domain.id}-txt-value`, domain.txt_record_value)
                                      }
                                      className="p-1 rounded border border-border hover:bg-secondary transition-colors shrink-0"
                                      title="Copy value"
                                    >
                                      {copiedField === `${domain.id}-txt-value` ? (
                                        <Check className="w-3 h-3 text-green-500" />
                                      ) : (
                                        <Copy className="w-3 h-3" />
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          After adding the records (DNS may take a few minutes to propagate), click
                          Verify. HTTPS is provisioned automatically within a minute of verification.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
